import {
  getAttributionScope,
  getAttributionStorage,
  getAttributionTarget,
  readAttributionRecord,
  writeAttributionRecord,
} from "./storage";
import type { AttributionStorageTarget } from "./storage";
import { getResolvedPid } from "./runtime";

export type KolEntryKind = "browser" | "pwa";
export type KolTrafficType = "production" | "preview" | "test";

export interface KolVisitContext {
  kolId?: string;
  linkId?: string;
  landingPageId?: string;
  visitId?: string;
  entryKind?: KolEntryKind;
  trafficType?: KolTrafficType;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  utmId?: string;
}

const QUERY_FIELDS = {
  kol_id: "kolId",
  link_id: "linkId",
  landing_page_id: "landingPageId",
  visit_id: "visitId",
  kol_entry: "entryKind",
  kol_traffic: "trafficType",
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_term: "utmTerm",
  utm_content: "utmContent",
  utm_id: "utmId",
} as const;

export const KOL_EM_PROPERTY_KEYS = [
  ...Object.values(QUERY_FIELDS),
  "appSurface",
  "trackingVersion",
  "acquisitionSource",
] as const;

interface KolVisitRecord {
  v: 1;
  collectedAtMs: number;
  context: KolVisitContext;
}

export const KOL_VISIT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const STORAGE_PREFIX = "webhall:kol-visit:v1:";
const runtimeContexts = new WeakMap<
  AttributionStorageTarget,
  { scope: string; record: KolVisitRecord }
>();

const getKolScope = (target: AttributionStorageTarget): string | undefined => {
  const scope = getAttributionScope(target);
  return scope
    ? `${scope}:${encodeURIComponent(getResolvedPid(target))}`
    : undefined;
};

const isValidField = (
  property: keyof KolVisitContext,
  value: unknown
): value is string => {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }

  if (property === "entryKind") {
    return value === "browser" || value === "pwa";
  }
  if (property === "trafficType") {
    return value === "production" || value === "preview" || value === "test";
  }

  return property.startsWith("utm")
    ? value.length <= 256 && !/[\u0000-\u001f\u007f]/.test(value)
    : /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value);
};

const isValidRecord = (
  value: unknown,
  nowMs: number
): value is KolVisitRecord => {
  const record = value as KolVisitRecord | undefined;
  if (
    record?.v !== 1 ||
    typeof record.collectedAtMs !== "number" ||
    !Number.isFinite(record.collectedAtMs) ||
    record.collectedAtMs <= 0 ||
    record.collectedAtMs > nowMs ||
    nowMs - record.collectedAtMs > KOL_VISIT_MAX_AGE_MS ||
    !record.context ||
    typeof record.context !== "object" ||
    Array.isArray(record.context)
  ) {
    return false;
  }

  const fields = Object.values(QUERY_FIELDS);
  const entries = Object.entries(record.context);
  return (
    entries.length > 0 &&
    entries.every(
      ([key, fieldValue]) =>
        fields.includes(key as keyof KolVisitContext) &&
        isValidField(key as keyof KolVisitContext, fieldValue)
    )
  );
};

const readIncomingContext = (
  params: URLSearchParams
): KolVisitContext | null | undefined => {
  const context: KolVisitContext = {};
  let hasCampaignParams = false;

  for (const [queryKey, property] of Object.entries(QUERY_FIELDS)) {
    if (!params.has(queryKey)) {
      continue;
    }
    hasCampaignParams = true;
    const values = params.getAll(queryKey);
    const value = values[0].trim();
    if (values.length !== 1 || (value && !isValidField(property, value))) {
      return null;
    }
    if (value) {
      Object.assign(context, { [property]: value });
    }
  }

  if (!hasCampaignParams) {
    return undefined;
  }
  return Object.keys(context).length ? context : null;
};

export const getKolVisitContext = (
  target?: AttributionStorageTarget,
  nowMs = Date.now()
): KolVisitContext => {
  const win = getAttributionTarget(target);
  const scope = win && getKolScope(win);
  if (!win || !scope) {
    return {};
  }

  const runtime = runtimeContexts.get(win);
  if (runtime?.scope === scope && isValidRecord(runtime.record, nowMs)) {
    return { ...runtime.record.context };
  }

  runtimeContexts.delete(win);
  const storage = getAttributionStorage(win, "localStorage");
  const key = `${STORAGE_PREFIX}${scope}`;
  const stored = readAttributionRecord(storage, key);
  if (!isValidRecord(stored, nowMs)) {
    writeAttributionRecord(storage, key);
    return {};
  }

  runtimeContexts.set(win, { scope, record: stored });
  return { ...stored.context };
};

/** Reporting metadata only; PID remains owned by validated native/domain attribution. */
export const bootstrapKolVisitContext = (
  target?: AttributionStorageTarget,
  nowMs = Date.now()
): KolVisitContext => {
  const win = getAttributionTarget(target);
  const scope = win && getKolScope(win);
  if (!win || !scope) {
    return {};
  }

  const incoming = readIncomingContext(new URL(win.location.href).searchParams);
  if (incoming === undefined) {
    return getKolVisitContext(win, nowMs);
  }

  const record: KolVisitRecord | undefined = incoming
    ? { v: 1, collectedAtMs: nowMs, context: incoming }
    : undefined;
  if (record) {
    runtimeContexts.set(win, { scope, record });
  } else {
    runtimeContexts.delete(win);
  }
  writeAttributionRecord(
    getAttributionStorage(win, "localStorage"),
    `${STORAGE_PREFIX}${scope}`,
    record
  );
  return incoming ? { ...incoming } : {};
};
